// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, cloneElement, Component, isValidElement } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Icon from '../Icon';
import Spinner from '../Spinner';
import Tooltip from '../Tooltip';
import oldIconNames from './old-icon-names.json';

import '../../helpers/base.less';
import './Button.less';


/**
 * `Button` component is used for executing actions, changing the application state, opening forms or dialog windows, and submitting data.
 * As a rule, you should not use buttons for navigation between individual pages (use links instead).
 */
class Button extends Component {
    constructor(props) {
        super(props);

        const toggle = props.onToggle !== null || props.selected !== null;
        this.state = {
            toggle,
            selected: toggle ? (props.selected || false) : null,
        };
    }

    componentWillReceiveProps(props) {
        const { toggle, selected } = this.state;
        const { onToggle, selected: selectedProp } = props;
        const newState = {};

        newState.toggle = onToggle !== null || selectedProp !== null;

        if (selectedProp === this.props.selected) {
            newState.selected = selected;
        } else {
            newState.selected = newState.toggle ? (selectedProp || false) : null;
        }

        if (newState.toggle !== toggle || newState.selected !== selected) {
            this.setState(newState);
        }
    }

    isResponsive() {
        return this.context.responsive || false;
    }

    handleToggle = () => {
        const { selected } = this.state;

        this.setState({ selected: !selected });

        return !selected;
    };

    renderIcon({ baseClassName, icon }) {
        return Icon.create(oldIconNames[icon] || icon, {
            className: `${baseClassName}__icon`,
        });
    }

    renderCaret({ baseClassName, caret }) {
        let caretElement;
        if (typeof caret === 'boolean' && caret) {
            caretElement = <span />;
        } else {
            caretElement = caret;
        }

        if (isValidElement(caretElement)) {
            caretElement = cloneElement(caretElement, {
                className: classNames(
                    `${baseClassName}__caret`,
                    caretElement.props.className,
                ),
            });
        }

        return caretElement;
    }

    render() {
        const {
            baseClassName,
            className,
            component: Tag,
            intent,
            selected: selectedProp,
            onToggle,
            state,
            icon,
            caret,
            tooltip,
            children,
            ...props
        } = this.props;

        const { toggle, selected } = this.state;
        const isCollapsed = this.isResponsive();

        const componentProps = {};
        if (Tag === 'button') {
            componentProps.type = 'button';
        } else if (Tag === 'a') {
            componentProps.role = 'button';
            componentProps.tabIndex = 0;

            if (props.onClick && props.disabled) {
                props.onClick = e => {
                    e.preventDefault();
                };
            }
        }

        let childrenElement = children;
        let tooltipElement = tooltip;
        if (isCollapsed && icon && children) {
            childrenElement = null;

            if (!tooltip) {
                tooltipElement = children;
            }
        }

        if (!props.onClick && onToggle) {
            props.onClick = e => {
                e.preventDefault();
                onToggle(this.handleToggle());
            };
        }

        if (toggle) {
            componentProps['aria-pressed'] = String(selected);
        }

        let button = (
            <Tag
                className={classNames(
                    baseClassName,
                    {
                        [`${baseClassName}--${intent}`]: intent,
                        [`${baseClassName}--${state}`]: state,
                        [`${baseClassName}--selected`]: selected,
                        [`${baseClassName}--disabled`]: props.disabled,
                        [`${baseClassName}--empty`]: !childrenElement,
                    },
                    className,
                )}
                {...componentProps}
                {...props}
            >
                {state === 'loading' ? <Spinner className={`${baseClassName}__spinner`} /> : this.renderIcon({ baseClassName, icon })}
                {childrenElement}
                {this.renderCaret({ baseClassName, caret })}
            </Tag>
        );

        if (tooltipElement) {
            button = (
                <Tooltip title={tooltipElement}>
                    {button}
                </Tooltip>
            );
        }

        return button;
    }
}

Button.propTypes = {
    /**
     * Visual intent color to apply to component.
     */
    intent: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'danger', 'info']),
    /**
     * Selected state of toggle button. Used in controlled component mode.
     */
    selected: PropTypes.bool,
    /**
     * Toggle button callback. Used in uncontrolled component mode.
     */
    onToggle: PropTypes.func,
    /**
     * State of the button
     */
    state: PropTypes.oneOf(['active', 'loading', 'hovered']),
    /**
     * Is button disabled?
     */
    disabled: PropTypes.bool,
    /**
     * Tooltip for component
     */
    tooltip: PropTypes.node,
    /**
     * Name of icon or [Icon](#!/Icon) component
     */
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.element,
    ]),
    /**
     * Show caret
     */
    caret: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.element,
    ]),
    /**
     * Content of the button
     */
    children: PropTypes.node,
    /**
     * Component to render as the root element. Useful when rendering a `Button` as `<a>` or `<Link>`.
     */
    component: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
    ]),
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
Button.defaultProps = {
    intent: null,
    selected: null,
    onToggle: null,
    state: null,
    disabled: false,
    tooltip: null,
    icon: null,
    caret: null,
    children: null,
    component: 'button',
    className: null,
    baseClassName: `${CLS_PREFIX}button`,
};

Button.contextTypes = {
    responsive: PropTypes.bool,
};

export default Button;



// WEBPACK FOOTER //
// ./components/Button/Button.js